import { inject, Injectable } from "@angular/core";
import { Origin, isOfType } from "../../../../models/types/general-types";
import { AccountView } from "../../../../models/view/account/account.view";
import { AccountStoreModel } from "../../../../models/store/account.store.model";
import { SourceAccountStoreModel } from "../../../../models/store/source-account-store.model";
import { SourceAccountView } from "../../../../models/view/source-book/source-account.view";
import { InstitutionView } from "../../../../models/view/institution/institution.view";
import { InstitutionAccountView } from "../../../../models/view/institution/institution-account.view";
import { AccountTypes } from "../../../../models/enum/account.enum";
import {
  getDefaultAccount,
  getOtherInstitutionId,
  isOtherInstitutionId,
} from "../../../../shared/utils/helper.institution/default-institution-account";
import {
  AllowedCurrencies,
  DefaultCurrencyPerLocation,
  DefaultSystemCurrency,
  DefaultSystemLocation,
  getLocationCurrencyFromCountryCode,
  type CurrencyType,
  type UserLocationType,
} from "../../../../models/types/location-currency.types";
import { DefaultInstitutionStoreModel } from "../../../../models/store/institution.store.model";
import { DataPluginStoreService } from "../../../store/data-plugin/data-plugin.store.service";
import { FiskilActions } from "./fiskil.actions";
import type { FiskilAccount } from "../types";
import type { FiskilBalance } from "../types/balance.types";

/** Maps Fiskil product_category to AccountType */
function fiskilProductCategoryToAccountType(productCategory?: string): string {
  const map: Record<string, string> = {
    TRANS_AND_SAVINGS_ACCOUNTS: AccountTypes.transaction,
    CRED_AND_CHRG_CARDS: AccountTypes.creditCard,
    TERM_DEPOSITS: AccountTypes.termDeposit,
    PERS_LOANS: AccountTypes.loan,
    RESIDENTIAL_MORTGAGES: AccountTypes.mortgage,
    MARGIN_LOANS: AccountTypes.loan,
    BUSINESS_LOANS: AccountTypes.loan,
    OVERDRAFTS: AccountTypes.transaction,
  };
  return map[productCategory] ?? AccountTypes.transaction;
}

@Injectable({
  providedIn: "root",
})
export class FiskilAccountAction extends FiskilActions {
  private dataPluginStore = inject(DataPluginStoreService);

  /**
   * Ensures vault has an InstitutionView per distinct Fiskil institution_id: prefers
   * DataPlugin reference list (`theInstitutionList`), then a minimal fallback.
   * Call before saving Fiskil accounts so instLnk can resolve.
   */
  async ensureFiskilInstitutionsInVault(fiskilAccounts: FiskilAccount[]): Promise<void> {
    const institutionIds: string[] = [
      ...new Set(
        fiskilAccounts
          .map((a) => (a.institution_id != null ? String(a.institution_id).trim() : ""))
          .filter((id): id is string => Boolean(id)),
      ),
    ];
    if (institutionIds.length === 0) {
      return;
    }

    const vaultInstitutions = this.userStore.institutions.institutionViews() ?? [];
    const missingIds: string[] = institutionIds.filter(
      (id) => !vaultInstitutions.some((v) => v.instoFiskilId === id),
    );
    if (missingIds.length === 0) {
      return;
    }

    const userLocation =
      this.userStore.preferences.preferenceView()?.userLocation ?? DefaultSystemLocation;
    const symbol = DefaultCurrencyPerLocation[userLocation];

    let referenceInstitutions = this.dataPluginStore.theInstitutionList.theInstitutionViews() ?? [];
    /** Reference list is filled when preferences load (`DataPluginStoreService`); fetch only if empty. */
    if (referenceInstitutions.length === 0) {
      referenceInstitutions =
        (await this.dataPluginStore.theInstitutionList.fetch({
          countryCode: userLocation,
          symbol,
        })) ?? [];
    }
    const toSave: InstitutionView[] = [];

    for (const id of missingIds) {
      const ref = referenceInstitutions.find((r) => r.instoFiskilId === id);
      if (ref) {
        toSave.push(this.referenceInstitutionToVaultCopy(ref));
      } else {
        toSave.push(this.createFallbackFiskilInstitution(id, userLocation));
      }
    }

    if (toSave.length > 0) {
      await this.userStore.institutions.saveToVault(toSave, true);
    }
  }

  /**
   * New vault institution from reference row: new cipher id, fresh account-product ids, Fiskil id preserved.
   */
  private referenceInstitutionToVaultCopy(ref: InstitutionView): InstitutionView {
    const model = ref.toStoreModel();
    model.id = crypto.randomUUID();
    model.vid = "";
    model.fk = "";
    model.fskId = ref.instoFiskilId;
    const now = new Date().toISOString();
    model.dc = now;
    model.dm = now;
    for (const acc of model.avaAcc) {
      acc.id = crypto.randomUUID();
    }
    return new InstitutionView(model);
  }

  private createFallbackFiskilInstitution(
    fiskilInstitutionId: string,
    userLocation: UserLocationType,
  ): InstitutionView {
    const locationCurrency = getLocationCurrencyFromCountryCode(userLocation);
    const institution = new InstitutionView({
      ...DefaultInstitutionStoreModel,
      id: crypto.randomUUID(),
      name: "Other",
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      fk: "",
      fskId: fiskilInstitutionId,
      swt: { bac: "XXX", cc: userLocation, lc: userLocation, brc: "YYY" },
    });
    institution.availableAccounts[0] = getDefaultAccount(institution.id, locationCurrency);
    return institution;
  }

  toAccountStoreModel(
    fiskilAccount: FiskilAccount,
    institution: InstitutionView,
    accountProduct: InstitutionAccountView,
    balance?: FiskilBalance,
  ): AccountStoreModel {
    const creditLimit = balance?.credit_limit ?? fiskilAccount.credit_card?.min_payment_amount ?? 0;
    return {
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      id: crypto.randomUUID(),
      v: 3,
      rtInstLnk: null,
      vid: "",
      instLnk: {
        institutionId: institution?.id,
        institutionAccountId: accountProduct?.id,
      },
      curr: this.toCurrencyType(fiskilAccount.bsb ? "AUD" : balance?.currency),
      name:
        fiskilAccount.display_name ??
        fiskilAccount.nickname ??
        fiskilAccount.product_name ??
        "Account",
      crLm: Number(creditLimit),
      bal: 0,
      tz: "Australia/Sydney",
      dfltCla: [],
      dfltCat: [],
      ori: Origin.fiskil,
      bsqId: fiskilAccount.account_id,
      date: new Date().toISOString(),
      type: fiskilProductCategoryToAccountType(fiskilAccount.product_category) as any,
      st: [],
    };
  }

  toSourceAccountStoreModel(
    fiskilAccount: FiskilAccount,
    accountStoreModel: AccountStoreModel,
  ): SourceAccountStoreModel {
    return {
      src:
        fiskilAccount.display_name ??
        fiskilAccount.nickname ??
        fiskilAccount.product_name ??
        "Account",
      acId: accountStoreModel.id,
      org: Origin.fiskil,
      bsqId: fiskilAccount.account_id,
      v: 1,
      vid: "",
      dc: new Date().toISOString(),
      dm: new Date().toISOString(),
      id: crypto.randomUUID(),
    };
  }

  /**
   * Source-book row for a Fiskil-linked vault account. Reuses cipher id when a row
   * already exists for this account (by vault id or Fiskil account_id) so sync updates
   * label + timestamps. Also reuses orphaned source rows left by interrupted imports.
   *
   * Orphans claimed earlier in the same import run are tracked in `claimedOrphanIds`
   * (#4181): the sync loop passes one shared Set across every iteration, so a second
   * Fiskil account sharing a label with the first cannot reuse the already-claimed
   * orphan. The Set is filtered in at selection time only — the #3491 strictness probe
   * still runs against the full label-matched population, so claiming one orphan can
   * never shrink that population and flip a later account into the legacy label-only path.
   */
  buildFiskilSourceAccountView(
    fiskilAccount: FiskilAccount,
    accountView: AccountView,
    existingFiskilSourceBooks: SourceAccountView[],
    existingAccounts: AccountView[] = [],
    claimedOrphanIds: Set<string> = new Set<string>(),
    allAccounts: AccountView[],
  ): SourceAccountView {
    const base = this.toSourceAccountStoreModel(fiskilAccount, accountView.toStoreModel());
    const sourceLabel = this.fiskilSourceLabel(fiskilAccount);

    const existing = existingFiskilSourceBooks.find((s) => {
      if (s.origin !== Origin.fiskil) {
        return false;
      }
      if (s.accountId === accountView.id) {
        return true;
      }
      const linked = existingAccounts.find((a) => a.id === s.accountId);
      return linked?.origin === Origin.fiskil && linked.basiqAccountId === fiskilAccount.account_id;
    });

    if (existing) {
      const existingModel = existing.toStoreModel();
      return new SourceAccountView({
        ...base,
        id: existingModel.id,
        vid: existingModel.vid,
        dc: existingModel.dc,
      });
    }

    // "Unclaimed by anyone" must be checked against the FULL account list (personal + org), not
    // the personal-only `existingAccounts` used above — a source row already linked to a real
    // org account isn't orphaned just because this personal refresh's candidate pool excludes
    // org accounts. Using the filtered list here would let this personal sync silently steal
    // (and overwrite) an org-owned source-account cipher's id/vid on a label collision.
    const labelMatchedOrphans = existingFiskilSourceBooks.filter(
      (s) =>
        s.origin === Origin.fiskil &&
        !allAccounts.some((a) => a.id === s.accountId) &&
        s.source === sourceLabel,
    );

    // Disambiguate cross-institution collisions (#3491): an orphan created after this fix
    // carries the Fiskil account_id it was originally meant for (see toSourceAccountStoreModel),
    // even though its own vault account never persisted. If any label match has that marker,
    // only a bsqId match may be reused — otherwise a same-named account at a different
    // institution could silently claim it. Orphans created before this fix have no bsqId to
    // check; when none of the label matches carry one, fall back to the old label-only match so
    // that legacy data keeps its existing (imperfect) recovery behavior.
    const bsqIdMatchedOrphans = labelMatchedOrphans.filter(
      (s) => s.bsqId != null && s.bsqId === fiskilAccount.account_id,
    );
    const candidateOrphans = (
      labelMatchedOrphans.some((s) => s.bsqId != null) ? bsqIdMatchedOrphans : labelMatchedOrphans
    ).filter((s) => !claimedOrphanIds.has(s.id));
    const orphan = candidateOrphans.length === 1 ? candidateOrphans[0] : undefined;
    if (orphan) {
      const orphanModel = orphan.toStoreModel();
      claimedOrphanIds.add(orphan.id);
      return new SourceAccountView({
        ...base,
        id: orphanModel.id,
        vid: orphanModel.vid,
        dc: orphanModel.dc,
      });
    }

    return new SourceAccountView(base);
  }

  private fiskilSourceLabel(fiskilAccount: FiskilAccount): string {
    return (
      fiskilAccount.display_name ??
      fiskilAccount.nickname ??
      fiskilAccount.product_name ??
      "Account"
    );
  }

  async getInstitutionForAccount(fiskilAccount: FiskilAccount): Promise<InstitutionView> {
    const institutions = this.userStore.institutions.institutionViews() ?? [];

    if (fiskilAccount.institution_id == null || fiskilAccount.institution_id === "") {
      return await this.getOrCreateUnknownPlaceholderInstitution(institutions);
    }

    const institutionIdKey = String(fiskilAccount.institution_id).trim();
    if (!institutionIdKey) {
      return await this.getOrCreateUnknownPlaceholderInstitution(institutions);
    }

    const matched = institutions.find((inst) => inst.instoFiskilId === institutionIdKey);
    if (matched) {
      return matched;
    }

    return await this.getOrCreateUnknownPlaceholderInstitution(institutions);
  }

  /** Last resort when institution_id is missing or not found after ensure step. */
  private async getOrCreateUnknownPlaceholderInstitution(
    institutions: InstitutionView[],
  ): Promise<InstitutionView> {
    const defaultInst = institutions.find((inst) => isOtherInstitutionId(inst.id));
    if (defaultInst) {
      return defaultInst;
    }

    const defaultInstitutionId = getOtherInstitutionId("AU");
    const defaultInstitution = new InstitutionView({
      ...DefaultInstitutionStoreModel,
      id: defaultInstitutionId,
      name: "Other",
      swt: { bac: "XXX", cc: "AU", lc: "AU", brc: "YYY" },
    });
    defaultInstitution.availableAccounts[0] = getDefaultAccount(
      defaultInstitutionId,
      DefaultCurrencyPerLocation.AU,
    );
    await this.userStore.institutions.saveToVault([defaultInstitution], true);
    return defaultInstitution;
  }

  getAccountProduct(
    fiskilAccount: FiskilAccount,
    institution: InstitutionView,
  ): InstitutionAccountView {
    const productName =
      fiskilAccount.product_name ?? fiskilAccount.product_category ?? "transaction";
    const accountProduct = institution?.availableAccounts?.find(
      (acc) => acc.name?.toLowerCase() === productName.toLowerCase(),
    );
    return (
      accountProduct ??
      institution?.availableAccounts?.[0] ??
      getDefaultAccount(institution?.id ?? "unknown", "AUD")
    );
  }

  /** Raw Fiskil balance row for an account, if present in the API response. */
  findFiskilBalanceForAccount(
    balances: FiskilBalance[],
    accountId: string,
  ): FiskilBalance | undefined {
    return balances.find((b) => b.account_id === accountId);
  }

  getBalanceForAccount(balances: FiskilBalance[], accountId: string): number {
    const balance = this.findFiskilBalanceForAccount(balances, accountId);
    if (!balance) {
      return 0;
    }
    const amt = balance.current_balance ?? balance.available_balance ?? "0";
    return Number(amt);
  }

  private toCurrencyType(raw: string | undefined): CurrencyType {
    return isOfType<CurrencyType>(raw, AllowedCurrencies) ? raw : DefaultSystemCurrency;
  }
}
